import React, { useRef, useState } from "react";
import { 
	RCCBadge, 
	RCCButton, 
	RCCGrid, 
	RCCTooltip, 
	RCCDropDownItem, 
	RCCIcon,
	RCCInput
} from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import Title from "src/components/Title";
import FiltersContainer from "src/components/FiltersContainer";
import { useTranslation } from "src/context/Translations/context";
import Pagination from "src/components/Pagination";
import Select from "src/components/Select";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import AppIcons from "src/components/AppIcons";
import { useAuth } from "src/context/Auth/context";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import Modals from "./modals";
import { IRefTypePeriodsModals } from "./interface";


const PeriodTypes: React.FC = () => { 
	const { translate } = useTranslation();
	const refModals = useRef<IRefTypePeriodsModals>(null);
	// Filters
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<{ filter: string ,active:string,coverage:string}>({ filter: "",active:"S", coverage :"T" });

	const { superUser } = useAuth();
	
	const { handleSubmit : handleSubmitFilters,
		control:controlFilters, 
		register : registerFilters,
		reset:resetFilters,
		watch:watchFilters } = useForm();

	const submitFilters = (data: any) => {
		setFilters({ 
			filter: data.filter,
			active:data.active.value,
			coverage:data.coverage.value
		});
		
		paginationRef.current?.reload();
	};

	// List & Pagination
	const paginationRef = useRef<IForwardPagination>(null);
	const colOptions = (row: any) => (
		<>
			<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
				{(superUser || row.iqb003Efa002Id) && (
					<OptionsColumn>
						<RCCDropDownItem
							selfEnclose
							onClick={() => { refModals.current?.actionButtonChange(row);  }}
						>
							<RCCIcon name="pencil" 
							/>
							{translate("Alterar")}
						</RCCDropDownItem>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
							<RCCDropDownItem 
								selfEnclose
								onClick={() => { refModals.current?.actionButtonDelete(row); }}
							>
								<RCCIcon name="trash" 
								/>
								{translate("Excluir")}
							</RCCDropDownItem>
						</CheckPermission>
					</OptionsColumn>
				)}
			</CheckPermission>
		</>
	);

	const colSituacao = (row: any) => (
		<RCCBadge color={row.iqb003Ativo ? "green" : "red"}
			styleInline="font-size: 0.83rem;"
		>
			{row.iqb003Ativo ? translate("Ativo") : translate("Inativo")}
		</RCCBadge>
	);

	const colAbrangencia = (row: any) => (
		<RCCBadge
			styleInline="font-size: 0.83rem;"
		>
			{row.abrangencia === "P" ? translate("Público") : translate("Empresa")}
		</RCCBadge>
	);

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				{/* Header -> Results + Buttons */}
				<RCCGrid
					container 
					item
					alignItems="center"
					justify="space-between"
				>
					<Title color="purple">
						<AppIcons name="configs" />
						{translate("Tipos de Período")}
					</Title>
					<div style={{
						display: "flex"
					}}>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							<RCCTooltip
								text={translate("Novo tipo de período")}
								color="purple"
							>
								<RCCButton
									icon={{name: "plus", color: "#FFF", size: 18}}
									color="purple"
									layout="isRounded"
									onClick={() => { refModals.current?.actionButtonAdd(); }}
									size="md"
								/>
							</RCCTooltip>
						</CheckPermission>
						<RCCTooltip
							text={translate("Filtros")}
							color={showFilters || filters.filter || filters.active !== "T" || filters.coverage !== "T" ? "blue" : "grey"}
						>
							<RCCButton
								icon={{name: "filter", color: "#FFF", size: 16}}
								color={showFilters || filters.filter || filters.active !== "T" || filters.coverage !== "T" ? "blue" : "grey"}
								layout="isRounded"
								size="md"
								onClick={() => setShowFilters((prevState) => !prevState)}
							/>
						</RCCTooltip>
					</div>
				</RCCGrid>

				{/* Filters */}
				{showFilters && (
					<FiltersContainer
						onSubmitFilters={handleSubmitFilters(submitFilters)}
						resetFilters={() => resetFilters({active: {value: "S", label: translate("Sim")},coverage:{value: "T", label: translate("Todos")}})}
					>
						<RCCGrid
							container
							item
						>
							<RCCGrid
								container
								item
								direction="row"
								spacing={1}
								xs={10}
							>
								<RCCGrid
									container 
									item
									xs={12}
									sm={4}
									md={3}
									lg={3}
								>
									<Select 
										control={controlFilters}
										name="active" 
										options={[
											{value: "S", label: translate("Sim")}, 
											{value: "N", label: translate("Não")},
											{value: "T", label: translate("Todos")}
										]} 
										label={translate("Ativos")}
										defaultValue={{value: "S", label: translate("Sim")}}
										submit="value and label"
									/>
								</RCCGrid>
								<RCCGrid
									container 
									item
									xs={12}
									sm={4}
									md={3}
									lg={3}
								>
									<Select 
										control={controlFilters}
										name="coverage" 
										options={[
											{value: "E", label: translate("Empresa")},
											{value: "T", label: translate("Todos")}
										]} 
										label={translate("Abrangência")}
										defaultValue={{value: "T", label: translate("Todos")}}
										submit="value and label"
									/>
								</RCCGrid>
								<RCCGrid
									container 
									item
									xs={12}
									sm={8}
									md={4}
									lg={4}
								>
									<RCCInput
										name="filter" label={translate("Pesquisar")}
										register={registerFilters}
										defaultValue={watchFilters("filter")}
									/>
								</RCCGrid>
							</RCCGrid>
						</RCCGrid>
					</FiltersContainer>
				)}

				{/* List */}
				<Pagination 
					ref={paginationRef}
					dataOrRoute="PeriodTypes/List"
					filtersData={filters}   
					columns={[
						{
							idColumn: "iqb003Codigo",
							nameColumn: translate("Código"),
							xs: 12,
							sm: 2,
							md: 2,
							type: "string"
						},
						{
							idColumn: "iqb003Descricao",
							nameColumn: translate("Descrição"),
							xs: 12,
							sm: 5,
							md: 5,
							type: "string"
						},
						{
							idColumn: "iqb003Ativo",
							nameColumn: translate("Situação"),
							xs: 12,
							sm: 2,
							md: 2,
							cellRenderer: colSituacao
						},
						{
							idColumn: "abrangencia",
							nameColumn: translate("Abrangência"),
							xs: 12,
							sm: 2,
							md: 2,
							cellRenderer: colAbrangencia
						},
						{
							idColumn: "options",
							nameColumn: " ",
							justify: "right",
							xs: 12,
							sm: 1,
							md: 1,
							cellRenderer: colOptions,
							disableSort: true
						}
					]}
				/>
			</RCCGrid>

			<Modals ref={refModals} paginationRef={paginationRef} />

		</>
	);
};

export default PeriodTypes;
